import { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Role } from '@src/services/role/roleTypes';
import { useLazyGetRoleListQuery } from '@src/services/role/roleList/roleListApiSlice';
import { useDeleteRoleMutation } from '@src/services/role/deleteRole/roleDeleteApiSlice';
import { SortDirection } from '@src/types/common';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LIST_LIMIT } from '@src/constants/pagination';

export function useRolesList() {
    const navigate = useNavigate();

    const [openRoleDeleteModal, setOpenRoleDeleteModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Number(searchParams.get('page') || 1);
    const pageSize = Number(searchParams.get('pageSize') || LIST_LIMIT);
    const searchText = searchParams.get('search') || '';
    const sortBy = searchParams.get('sortBy') || null;
    const sortOrder = (searchParams.get('sortOrder') as SortDirection) || null;

    const [getRoles, { data, isLoading, isError, isFetching, status }] =
        useLazyGetRoleListQuery();
    const [deleteRole, { isLoading: isDeleting }] = useDeleteRoleMutation();

    const fetchRoles = useCallback(
        _.debounce((params: any) => {
            getRoles(params);
        }, 500),
        [getRoles]
    );
    useEffect(() => {
        const trimmedSearch = searchText?.trim();
        if (trimmedSearch && trimmedSearch.length < 3) return;

        fetchRoles({
            page,
            limit: pageSize,
            search: searchText || undefined,
            sortBy: sortBy || undefined,
            sortOrder: sortOrder || undefined,
        });
    }, [searchText, page, pageSize, sortBy, sortOrder]);

    const updateSearchParams = (
        params: Record<string, string | number | null | undefined>
    ) => {
        const newParams = {
            page,
            pageSize,
            search: searchText,
            sortBy,
            sortOrder,
            ...params,
        };

        Object.keys(newParams).forEach((key) => {
            if (
                newParams[key] === null ||
                newParams[key] === undefined ||
                newParams[key] === ''
            ) {
                searchParams.delete(key);
            } else {
                searchParams.set(key, String(newParams[key]));
            }
        });

        setSearchParams(searchParams);
    };

    const handleChangePagination = (newPage: number, newPageSize: number) => {
        updateSearchParams({ page: newPage, pageSize: newPageSize });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        updateSearchParams({ search: val, page: 1 });
    };

    const handleActionClick = (action: 'details' | 'delete', item: Role) => {
        if (!item) return;

        setSelectedRole(item);
        switch (action) {
            case 'details':
                navigate(`${item.id}/details?readonly=${item.isMainRole}`);
                break;
            case 'delete':
                setOpenRoleDeleteModal(true);
                break;
        }
    };

    const confirmDelete = async () => {
        if (selectedRole) {
            await deleteRole({
                id: selectedRole.id,
            }).unwrap();
            setOpenRoleDeleteModal(false);
        }
    };

    const handleSortChange = (
        columnKey: string,
        order: SortDirection | null
    ) => {
        updateSearchParams({ sortBy: columnKey, sortOrder: order, page: 1 });
    };

    return {
        data,
        isLoading,
        isFetching,
        isDeleting,
        isError,
        searchText,
        selectedRole,
        status,
        openRoleDeleteModal,
        sortBy,
        sortOrder,
        setOpenRoleDeleteModal,
        setSelectedRole,
        handleActionClick,
        handleSearchChange,
        confirmDelete,
        handleChangePagination,
        handleSortChange,
    };
}
